/**
 * Razorpay Standard Checkout helper. Loads the SDK on demand and opens the
 * payment modal. Resolves with the payment response (verified server-side)
 * or rejects if the user closes the modal / payment fails.
 */
import api from "@/lib/api";

const SDK_SRC = "https://checkout.razorpay.com/v1/checkout.js";
let sdkPromise = null;

function loadSdk() {
  if (window.Razorpay) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("Failed to load Razorpay checkout SDK"));
    };
    document.body.appendChild(script);
  });
  return sdkPromise;
}

/**
 * Open Razorpay Checkout for a Smart S Notes payment.
 * @param {object} opts
 * @param {"subscription"|"credits"} opts.kind
 * @param {number} [opts.videos] - required when kind === "credits"
 * @returns {Promise<{verified: boolean, kind: string, videos?: number, publicSub: object}>}
 */
export async function payWithRazorpay({ kind, videos }) {
  await loadSdk();
  // 1) Ask the backend for an order
  const { data: order } = await api.post("/payments/create-order", { kind, videos });
  // 2) Open the checkout modal
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Smart S Notes",
      description: order.description,
      order_id: order.order_id,
      prefill: order.prefill || {},
      theme: { color: "#3B82F6" },
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled")),
      },
      handler: async (resp) => {
        try {
          const { data: verified } = await api.post("/payments/verify", {
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_signature: resp.razorpay_signature,
          });
          resolve({
            verified: !!verified.ok,
            kind: verified.kind,
            videos: verified.videos,
            publicSub: verified.public,
          });
        } catch (err) {
          reject(err);
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      const err = resp?.error;
      reject(new Error(err?.description || "Payment failed"));
    });
    rzp.open();
  });
}
